/**
 * Whether a live model's scores actually reach a CRM, and where.
 *
 * Three states, and the third is the reason this is not a boolean:
 *
 *   a count, with the destinations on hover, when attributes are enabled;
 *   "Not pushed", when the model is deployed and nothing is enabled, which
 *   means it scores every night into tables nobody reads;
 *   "Unknown", when the push config cannot speak for this model at all.
 *
 * The last one is secondary (multi) customer-fit models. They write their own
 * config folder but are never named in the account or contact push attributes,
 * so nothing here can conclude anything about them. Drawing that as "Not
 * pushed" would assert something no data supports, about the one subject where
 * being wrong is expensive: whether customer data is leaving the building.
 */
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { ModelPushStatus } from './modelsHomeApi';
import './pushChip.css';

function destinationList(status: ModelPushStatus) {
  return (
    <ul className="mhpush-list">
      {status.destinations.map((destination) => (
        <li key={`${destination.connector}-${destination.object}`}>
          <span>{destination.connector}</span>
          <em>{destination.object}</em>
        </li>
      ))}
    </ul>
  );
}

export function PushChip({ status }: { status?: ModelPushStatus }) {
  // No connector answered. Same reasoning as the unknown case below: silence
  // from the source is not evidence about the model.
  if (!status) {
    return <span className="mhpush mhpush-unknown">Unknown</span>;
  }

  if (!status.known) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="push-unknown">
            Nothing in the push configuration names this model, so whether its
            scores reach a CRM cannot be determined here.
          </Tooltip>
        }
      >
        <span className="mhpush mhpush-unknown">Unknown</span>
      </OverlayTrigger>
    );
  }

  if (!status.destinations.length) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="push-none">
            This model is not pushed to any integration.
          </Tooltip>
        }
      >
        <span className="mhpush mhpush-off">
          <i aria-hidden className="mhpush-dot" /> Not pushed
        </span>
      </OverlayTrigger>
    );
  }

  const objects = status.destinations.length;
  return (
    <span className="mhpush-wrap">
      <span className="mhpush mhpush-on">
        <i aria-hidden className="mhpush-dot" />
        {objects} {objects === 1 ? 'object' : 'objects'}
      </span>
      <span className="mhpush-pop" role="tooltip">
        <span className="mhpush-ph">Scores pushed to</span>
        {destinationList(status)}
      </span>
    </span>
  );
}

export default PushChip;
